<script setup lang="ts">
// Bottom-nav de 5 abas (formato celular) — porte de `.nav`/`.nav-i`/`.nav-ico`/`.nav-l`/`.nav-dot`
// (Docs/V2/Clinica/clinica/styles.css + app.jsx). Geometria literal do protótipo.
//
// Além do protótipo (e por quê):
// - `<RouterLink>` em vez de `<button onClick>`: item de navegação precisa ser link real (abrir em
//   nova aba, ler como link por leitor de tela, estado ativo derivado da rota e não de estado local);
// - `aria-current="page"` no item ativo e `aria-hidden` no glifo, que é decorativo;
// - o contador ganha rótulo textual acessível — "3" sozinho não diz nada fora do contexto visual.
import { RouterLink } from 'vue-router';
import type { BadgeKey, Destination } from '@/router/destinations';

const props = defineProps<{
  items: readonly Destination[];
  /** Contadores vindos de `GET /portal-summary`. Nunca contados no cliente. */
  badges: Readonly<Partial<Record<BadgeKey, number>>>;
  /** Nome do destino aceso — pode ser um pai (a proposta acende "Meu plano"). */
  activeName: string | null;
}>();

function badgeOf(item: Destination): number {
  return item.badgeKey === undefined ? 0 : (props.badges[item.badgeKey] ?? 0);
}
</script>

<template>
  <nav
    class="nav"
    aria-label="Navegação principal"
  >
    <RouterLink
      v-for="item in items"
      :key="item.name"
      class="nav-i"
      :class="{ on: item.name === activeName }"
      :to="item.path"
      :aria-current="item.name === activeName ? 'page' : undefined"
    >
      <span
        class="nav-ico"
        aria-hidden="true"
      >{{ item.icon }}</span>
      <span class="nav-l">{{ item.title }}</span>
      <span
        v-if="badgeOf(item) > 0"
        class="nav-dot"
      >
        <span aria-hidden="true">{{ badgeOf(item) }}</span>
        <span class="nav-sr">{{ badgeOf(item) }} pendência(s) em {{ item.title }}</span>
      </span>
    </RouterLink>
  </nav>
</template>

<style scoped>
/* Transcrição literal de `.nav*` (clinica/styles.css). Cor por token, geometria como no protótipo. */
.nav {
  display: flex;
  background: var(--dt-surface);
  border-top: 1px solid var(--dt-divider);
  flex-shrink: 0;
  padding-bottom: 24px;
}
/* Os 24px do protótipo são a faixa do indicador de home desenhada pela moldura de iPhone. Em
   aparelho real vale o inset de verdade; sem inset, o fallback reproduz os 24px do protótipo. */
@supports (padding: env(safe-area-inset-bottom)) {
  .nav { padding-bottom: env(safe-area-inset-bottom, 24px); }
}
.nav-i {
  flex: 1;
  background: none;
  border: none;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 3px;
  padding: 8px 2px 6px;
  cursor: pointer;
  position: relative;
  min-height: 48px;
  text-decoration: none;
  color: inherit;
  transition: transform var(--dt-motion-fast) var(--dt-motion-ease);
}
.nav-i:active {
  transform: scale(0.92);
}
.nav-ico {
  font-size: 21px;
  line-height: 1;
  transition: transform var(--dt-motion-fast) cubic-bezier(0.34, 1.56, 0.64, 1);
}
.nav-i.on .nav-ico {
  transform: scale(1.12);
}
.nav-l {
  font-size: 10px;
  font-weight: var(--dt-font-weight-bold);
  color: var(--dt-text-secondary);
  transition: color var(--dt-motion-fast) var(--dt-motion-ease);
}
.nav-i.on .nav-l { color: var(--dt-blue); }
.nav-dot {
  position: absolute;
  top: 5px;
  right: calc(50% - 18px);
  min-width: 15px;
  height: 15px;
  padding: 0 4px;
  border-radius: 20px;
  background: var(--dt-error);
  color: #fff;
  font-size: 9px;
  font-weight: var(--dt-font-weight-extrabold);
  display: flex;
  align-items: center;
  justify-content: center;
}
.nav-sr { position: absolute; width: 1px; height: 1px; overflow: hidden; clip-path: inset(50%); white-space: nowrap; }
</style>
